import React, { useEffect, useMemo, useState } from "react";
import { Paper, Typography, Box, Skeleton, Divider, Button } from "@mui/material";
import SemiCircleProgress from "react-progressbar-semicircle";
import { useVision360Cache } from "../../context/Vision360Context";

const FRAUD_API_BASE = (process.env.REACT_APP_BACKEND_URL_MS_FRAUDINDICATORS || "").replace(/\/+$/, "");

const formatIndicatorKey = (key = "") =>
  key
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const buildSinistrePayload = (sinistre) => {
  if (!sinistre) return null;
  const payload = { ...sinistre };
  payload.claimNumber = payload.claimNumber || payload.numero_sinistre;
  payload.numero_sinistre = payload.numero_sinistre || payload.claimNumber;
  payload.date_sinistre =
    payload.date_sinistre ||
    (typeof payload.occurrenceDate === "string" ? payload.occurrenceDate.split("T")[0] : payload.occurrenceDate) ||
    null;
  payload.montant_sinistre =
    payload.montant_sinistre ?? payload.claimAmount ?? payload.totalAmount ?? payload.indemnityAmount ?? 0;
  if (!payload.existe_rapport_authorité) {
    if (payload.hasAuthorityReport === false) {
      payload.existe_rapport_authorité = "NON";
    } else if (payload.hasAuthorityReport === true) {
      payload.existe_rapport_authorité = "OUI";
    }
  }
  if (!payload.TiersAdverse && Array.isArray(payload.involvedParties)) {
    const opponents = payload.involvedParties.filter((party) =>
      ["THIRD_PARTY", "OPPONENT", "TIERS_ADVERSE"].includes((party?.role || "").toUpperCase())
    );
    payload.TiersAdverse = opponents;
  }
  return payload;
};

export default function UnfraudedSection({ claimNumber, infoSinistre, clientUid, carUids = [] }) {
  const [fraudResult, setFraudResult] = useState(null);
  const [gptReport, setGptReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingGptReport, setLoadingGptReport] = useState(false);
  const [error, setError] = useState(null);
  const { fetchClient, fetchFirstAvailableCar, getClient, getCar } = useVision360Cache();

  const firstCarUid = useMemo(() => carUids?.[0] || null, [carUids]);

  const indicatorResults = useMemo(() => {
    const indicators = fraudResult?.fraudIndicators || {};
    return Object.entries(indicators)
      .map(([key, meta]) => ({
        id: key,
        title: formatIndicatorKey(key),
        description: meta?.description || "",
        score: meta?.value,
        displayType: meta?.displayType,
      }))
      .sort((a, b) => {
        const aScore = Number.isFinite(Number(a.score)) ? Number(a.score) : -Infinity;
        const bScore = Number.isFinite(Number(b.score)) ? Number(b.score) : -Infinity;
        return bScore - aScore;
      });
  }, [fraudResult]);

  const globalScore = useMemo(() => {
    const value = parseFloat(fraudResult?.globalScore);
    return Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : 0;
  }, [fraudResult]);

  const formattedAnalysisDate = useMemo(() => {
    const rawDate = fraudResult?.computedAt || fraudResult?.generatedAt;
    if (!rawDate) return null;
    const dateObj = new Date(rawDate);
    if (Number.isNaN(dateObj.getTime())) return null;
    return dateObj.toLocaleString();
  }, [fraudResult]);

  const isFraudFlagged = Boolean(fraudResult?.fraudFlag);

  const fraudProbability = useMemo(() => {
    if (gptReport?.fraudProbability) {
      const value = parseFloat(gptReport.fraudProbability);
      return Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : 0;
    }
    return globalScore;
  }, [gptReport, globalScore]);

  const formatIndicatorScore = (score, displayType) => {
    if (score === null || score === undefined || Number.isNaN(Number(score))) {
      return "N/A";
    }
    const numericScore = Number(score);
    if ((displayType || "").toUpperCase() === "PERCENTAGE") {
      return `${numericScore.toFixed(2)}%`;
    }
    return numericScore.toFixed(2);
  };

  useEffect(() => {
    let cancelled = false;

    const fetchFraudIndicators = async () => {
      if (!claimNumber || !infoSinistre || !FRAUD_API_BASE) {
        setFraudResult(null);
        setError(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const [clientData, carResult] = await Promise.all([
          clientUid ? fetchClient(clientUid).catch(() => getClient(clientUid)) : Promise.resolve(null),
          carUids.length ? fetchFirstAvailableCar(carUids) : Promise.resolve({ carUid: null, data: null }),
        ]);

        const visionClient = clientData || (clientUid ? getClient(clientUid) : null);
        let visionVehicle = carResult?.data;
        if (!visionVehicle && firstCarUid) {
          const cached = getCar(firstCarUid);
          if (cached?.car) {
            visionVehicle = cached;
          }
        }

        if (!visionVehicle?.car && carUids.length) {
          console.warn("[UnfraudedSection] Vision360 car data unavailable for UIDs:", carUids);
        }

        const sinistrePayload = buildSinistrePayload(infoSinistre);
        if (!sinistrePayload?.numero_sinistre) {
          throw new Error("Impossible de lancer la détection sans numéro de sinistre.");
        }

        const payload = {
          sinistre: sinistrePayload,
          police: visionVehicle?.policy || infoSinistre?.involvedPolicies?.[0] || null,
          visionClient: visionClient || null,
          visionVehicle: visionVehicle || null,
        };

        const token = localStorage.getItem("token");
        const headers = {
          "Content-Type": "application/json",
        };
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const response = await fetch(`${FRAUD_API_BASE}/fraud-detection`, {
          method: "POST",
          headers,
          body: JSON.stringify(payload),
        });

        if (cancelled) return;

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        setFraudResult(data);
        
        // Fetch GPT report after fraud detection completes
        if (!cancelled && claimNumber) {
          fetchGptReport(claimNumber, payload);
        }
      } catch (err) {
        if (cancelled) return;
        console.error("[UnfraudedSection] Fraud detection error:", err);
        setFraudResult(null);
        setError(err?.message || "fraud-detection failed");
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    const fetchGptReport = async (claimNum, fraudPayload) => {
      if (!FRAUD_API_BASE || !claimNum) return;
      
      setLoadingGptReport(true);
      try {
        // Prepare claim data as JSON string for GPT analysis
        const claimDataJson = JSON.stringify({
          sinistre: fraudPayload.sinistre,
          police: fraudPayload.police,
          visionClient: fraudPayload.visionClient,
          visionVehicle: fraudPayload.visionVehicle,
        });

        const token = localStorage.getItem("token");
        const headers = {
          "Content-Type": "application/json",
        };
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const response = await fetch(`${FRAUD_API_BASE}/get_gpt_fraud_analysis_report`, {
          method: "POST",
          headers,
          body: JSON.stringify({
            claimData: claimDataJson,
            relatedClaimNumber: claimNum,
            context: "GAM_FRAUD_ANALYSIS_PROMPT",
          }),
        });

        if (cancelled) return;

        if (response.ok) {
          const result = await response.json();
          setGptReport(result?.report || result);
        } else {
          console.warn("[UnfraudedSection] GPT report not available or error:", response.status);
        }
      } catch (err) {
        if (cancelled) return;
        console.warn("[UnfraudedSection] GPT report fetch error:", err);
        // Don't set error state for GPT report failures, just log
      } finally {
        if (!cancelled) {
          setLoadingGptReport(false);
        }
      }
    };

    fetchFraudIndicators();

    return () => {
      cancelled = true;
    };
  }, [claimNumber, infoSinistre, clientUid, firstCarUid, carUids, fetchClient, fetchFirstAvailableCar, getClient, getCar]);

  return (
    <Box fontFamily={"Poppins"} sx={{ display: "flex", gap: 2, padding: "24px" }}>
      {/* Détection Section */}
      <Box sx={{ flex: 2, borderRadius: 8 }}>
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #E0E0E0",
            borderRadius: "11.64px",
            marginBottom: 2,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              padding: "16px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography fontSize="18px" fontWeight="500" color="#212121" fontFamily={"Poppins"}>
              Détection
            </Typography>
            {/* <Button
              variant="contained"
              size="small"
              disableElevation
              sx={{
                padding: "6px 16px",
                backgroundColor: "#1BC5BD",
                color: "#fff",
                textTransform: "none",
                borderRadius: "6px",
                fontSize: "12px",
                fontWeight: "500",
                fontFamily: "Poppins",
                "&:hover": {
                  backgroundColor: "#1BC5BD",
                },
              }}
            >
              Details
            </Button> */}
          </Box>
          <Divider />
          <Box>
            {loading ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {[...Array(3)].map((_, index) => (
                  <Skeleton key={index} variant="rounded" height={56} />
                ))}
              </Box>
            ) : indicatorResults.length ? (
              indicatorResults.map((indicator) => (
                <Box
                  key={indicator.id}
                  sx={{
                    padding: "16px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 2,
                    borderBottom: "1px solid #E0E0E0",
                  }}
                >
                  <Box sx={{ pr: 2 }}>
                    <Typography fontSize="12px" fontWeight="300" color="#464E5F" fontFamily={"Poppins"}>
                      {indicator.description || indicator.title}
                    </Typography>
                  </Box>
                  <Typography fontSize="12px" fontWeight="600" color="#3F4254" fontFamily={"Poppins"}>
                    {formatIndicatorScore(indicator.score, indicator.displayType)}
                  </Typography>
                </Box>
              ))
            ) : (
              <Typography fontSize="13px" fontWeight="400" color="#A3A3A3" fontFamily={"Poppins"} sx={{ padding: "16px" }}>
                Aucune règle de fraude n'a été déclenchée pour ce sinistre pour le moment.
              </Typography>
            )}
          </Box>
        </Paper>
      </Box>

      {/* Résumé de l'analyse Section */}
      <Box sx={{ flex: 1 }}>
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #E0E0E0",
            borderRadius: "11.64px",
          }}
        >
          <Box
            sx={{
              paddingX: "16px",
              paddingY: "12px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography fontSize="18px" fontWeight="500" color="#212121" fontFamily={"Poppins"}>
              Résumé de l'analyse
            </Typography>
            <SemiCircleProgress
              percentage={fraudProbability}
              diameter={100}
              showPercentValue
              stroke={isFraudFlagged || (gptReport?.fraudFlag === true) ? "#F64E60" : "#1BC5BD"}
              strokeWidth={6}
            />
          </Box>
          <Divider />
          {loadingGptReport ? (
            <Box sx={{ display: "flex", flexDirection: "column", padding: "16px" }}>
              <Skeleton variant="text" width="60%" height={30} />
              <Skeleton variant="circular" width={60} height={60} sx={{ marginTop: 2 }} />
              <Skeleton variant="text" width="80%" height={21} sx={{ marginTop: 2 }} />
              <Skeleton variant="text" width="80%" height={21} sx={{ marginTop: 2 }} />
              <Skeleton variant="text" width="80%" height={21} sx={{ marginTop: 2 }} />
            </Box>
          ) : (
            <Box sx={{ padding: "16px" }}>
              <Typography
                sx={{
                  color: "#3769A4",
                  fontFamily: "Poppins",
                  fontSize: "15.52px",
                  fontWeight: 400,
                  lineHeight: "22.5px",
                  textAlign: "left",
                  textUnderlinePosition: "from-font",
                  textDecorationSkipInk: "none",
                }}
                mb={2}
              >
                Comportement de l'Assuré
              </Typography>
              <Typography fontSize="13px" fontWeight="350" mb={2} color="#464E5F" fontFamily={"Poppins"}>
                {gptReport?.customerBehaviorAnalysis || "Aucune analyse disponible."}
              </Typography>

              <Typography
                sx={{
                  color: "#3769A4",
                  fontFamily: "Poppins",
                  fontSize: "15.52px",
                  fontWeight: 400,
                  lineHeight: "22.5px",
                  textAlign: "left",
                  textUnderlinePosition: "from-font",
                  textDecorationSkipInk: "none",
                }}
                mb={2}
              >
                Analyse du Véhicule
              </Typography>
              <Typography fontSize="13px" fontWeight="350" mb={2} color="#464E5F" fontFamily={"Poppins"}>
                {gptReport?.carBehaviorAnalysis || "Aucune analyse disponible."}
              </Typography>

              <Typography
                sx={{
                  color: "#3769A4",
                  fontFamily: "Poppins",
                  fontSize: "15.52px",
                  fontWeight: 400,
                  lineHeight: "22.5px",
                  textAlign: "left",
                  textUnderlinePosition: "from-font",
                  textDecorationSkipInk: "none",
                }}
                mb={2}
              >
                Antécédents Connexes
              </Typography>
              <Typography fontSize="13px" fontWeight="350" mb={2} color="#464E5F" fontFamily={"Poppins"}>
                {gptReport?.similarClaimExistanceInHistory || "Aucune donnée disponible."}
              </Typography>

              <Typography
                sx={{
                  color: "#3769A4",
                  fontFamily: "Poppins",
                  fontSize: "15.52px",
                  fontWeight: 400,
                  lineHeight: "22.5px",
                  textAlign: "left",
                  textUnderlinePosition: "from-font",
                  textDecorationSkipInk: "none",
                }}
                mb={2}
              >
                Conclusion
              </Typography>
              <Typography fontSize="13px" fontWeight="350" mb={2} color="#464E5F" fontFamily={"Poppins"}>
                {gptReport?.conclusion || "Aucune conclusion disponible."}
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
}
